import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    AppBar,
    Toolbar,
    IconButton,
    Box,
    Button,
    Tooltip,
    Menu,
    MenuItem,
    Divider,
    ListItemIcon,
    ListItemText,
    ListSubheader,
    Typography,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PhoneInTalkIcon from '@mui/icons-material/PhoneInTalk';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import PositionedMenu from './accountMenu';
import { UserContext } from '../auth/auth-context';
import { StyledLink } from '../../assets/css/styledComponent';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelectedBusiness } from '../../redux/store';
import { getTrialDaysLeft } from '../../util/utils';
import PlanyBot from '../../../products/conversationalBot/components/common/planyBot';
import NotificationBadge from '../reminder/notificationBadge';
import { detectVerticalFromPath, isVerticalId } from '../../verticals/registry';
import { getStoredVertical } from '../../routes/verticalPref';
import Logo from '../../assets/images/plany365.ico';
import { PlanyBrandMark } from './planyBrandMark';
import {
    buildHttpsUrl,
    getBrandVertical,
    getPublicHostForVertical,
    shouldCrossDomainNavigate,
} from '../../util/brandDomains';
import { APP_IDS, getRuntimeAppProfile } from '../../runtime/appProfile';

const BusinessHeader = () => {
    const theme = useMuiTheme();
    const chrome = theme.customChrome || {};
    const MOBILE_TOOLBAR_H = 48;
    const DESKTOP_TOOLBAR_H = 58;
    const META_ROW_H = 18;
    const { providerUser } = useContext(UserContext);
    const location = useLocation();
    const navigate = useNavigate();
    const selectedBusiness = useSelectedBusiness();
    const currentStaffImage = selectedBusiness?.businessStaffs?.find(
        (staff) => Number(staff.id) === providerUser?.staffId
    )?.image;

    const navLinkSx = {
        color: 'white',
        fontSize: '0.8125rem',
        fontWeight: 500,
        lineHeight: 1.75,
        whiteSpace: 'nowrap',
    };

    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const openMobileMenu = (e) => setMobileMenuAnchor(e.currentTarget);
    const closeMobileMenu = () => setMobileMenuAnchor(null);
    const [customerMenuAnchor, setCustomerMenuAnchor] = useState(null);
    const closeCustomerMenu = () => setCustomerMenuAnchor(null);
    const [callAnchor, setCallAnchor] = useState(null);
    const openCallMenu = (e) => setCallAnchor(e.currentTarget);
    const closeCallMenu = () => setCallAnchor(null);
    const pricingLinkState = useMemo(
        () => ({ from: `${location.pathname}${location.search}${location.hash}` }),
        [location.pathname, location.search, location.hash]
    );
    const brandVertical = useMemo(() => getBrandVertical(), []);
    const isTimelyApp = useMemo(
        () => getRuntimeAppProfile().id === APP_IDS.TIMELY,
        []
    );
    const activeProductId = useMemo(() => {
        const detected = detectVerticalFromPath(location.pathname);
        if (detected) return detected;
        if (brandVertical) return brandVertical;
        const params = new URLSearchParams(location.search);
        const verticalFromQuery = params.get("v");
        if (verticalFromQuery && isVerticalId(verticalFromQuery)) return verticalFromQuery;
        const stored = getStoredVertical();
        if (stored && isVerticalId(stored)) return stored;
        return "care";
    }, [brandVertical, location.pathname, location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        let changed = false;

        if (params.get("v") && params.get("v") !== activeProductId) {
            params.set("v", activeProductId);
            changed = true;
        }
        if (params.get("product") && params.get("product") !== activeProductId) {
            params.set("product", activeProductId);
            changed = true;
        }
        if (!changed) return;

        const nextSearch = params.toString();
        navigate(
            `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}${location.hash}`,
            { replace: true }
        );
    }, [activeProductId, location.hash, location.pathname, location.search, navigate]);
    const subscriptionsPath = `/biz/pricing?product=${activeProductId}`;
    const customerViewLocalPath = activeProductId === "booking" ? "/booking" : "/care";
    const customerViewTargetHost = getPublicHostForVertical(activeProductId);
    const customerViewPath = shouldCrossDomainNavigate(customerViewTargetHost)
        ? buildHttpsUrl(customerViewTargetHost, "/")
        : customerViewLocalPath;
    const customerViewItems = [
        { label: 'Booking Page', path: `${customerViewPath}${customerViewPath.includes('?') ? '&' : '?'}v=booking` },
        { label: 'Capture Inbox', path: '/biz/capture?v=booking' },
    ];
    const renderCustomerViewItems = (closeMenu) => customerViewItems.map((item) => (
        <MenuItem key={item.label} component={RouterLink} to={item.path}
            target="_blank" rel="noopener noreferrer" onClick={closeMenu}>
            <ListItemText primary={item.label} secondary="Opens in a new tab" />
            <ListItemIcon sx={{ ml: 2, minWidth: 'auto' }}><OpenInNewRoundedIcon fontSize="small" /></ListItemIcon>
        </MenuItem>
    ));
    const businessName = selectedBusiness?.businessBasic?.name || "";
    const trialDaysLeft = getTrialDaysLeft(providerUser || {});
    const trialLabel =
        trialDaysLeft === "Unlimited"
            ? "Business: Unlimited trial"
            : Number(trialDaysLeft) > 0
                ? `Business: ${Number(trialDaysLeft)} day${Number(trialDaysLeft) === 1 ? "" : "s"} left in trial`
                : "";
    const showMetaRow = Boolean(businessName || trialLabel);
    const appBarRef = useRef(null);
    const [headerSpacerHeight, setHeaderSpacerHeight] = useState(
        MOBILE_TOOLBAR_H + (showMetaRow ? META_ROW_H : 0)
    );
    const PHONES = [
        { label: "Call Plany Assist: (833) 368-1399", tel: "tel:+18333681399" },
    ];
    const topActionSx = {
        whiteSpace: "nowrap",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0.8,
        px: 1.8,
        py: 0.78,
        borderRadius: "999px",
        border: `1px solid ${chrome.actionBorder || "rgba(255,255,255,0.14)"}`,
        backgroundColor: chrome.actionBg || "rgba(255,255,255,0.06)",
        color: chrome.actionText || "#f5f8fc",
        textTransform: "none",
        letterSpacing: "0.01em",
        fontWeight: 600,
        fontSize: "0.82rem",
        lineHeight: 1,
        textDecoration: "none",
        boxShadow: "0 10px 24px rgba(6, 16, 28, 0.12)",
        backdropFilter: "blur(16px)",
        transition: "all 180ms ease",
        "& .MuiSvgIcon-root": { color: chrome.actionText || "#f5f8fc", fontSize: 18 },
        "&:hover": {
            borderColor: chrome.actionBorder || "rgba(255,255,255,0.24)",
            backgroundColor: chrome.actionHoverBg || "rgba(255,255,255,0.12)",
            color: chrome.actionHoverText || "#ffffff",
            transform: "translateY(-1px)",
            "& .MuiSvgIcon-root": { color: chrome.actionHoverText || "#ffffff" },
        },
    };
    const switchToCustomerHelp = "Leave Business workspace and open Customer view.";

    useEffect(() => {
        const measureHeader = () => {
            const next = appBarRef.current?.offsetHeight || 0;
            const measuredHeight = Math.max(0, next - 1);
            setHeaderSpacerHeight(measuredHeight);
            if (typeof document !== "undefined") {
                document.documentElement.style.setProperty(
                    "--business-header-height",
                    `${measuredHeight}px`
                );
            }
        };

        const frame = window.requestAnimationFrame(measureHeader);
        window.addEventListener("resize", measureHeader);
        return () => {
            window.cancelAnimationFrame(frame);
            window.removeEventListener("resize", measureHeader);
        };
    }, [providerUser?.userId, showMetaRow]);


    return (
        <>
            <AppBar
                ref={appBarRef}
                position="fixed"
                sx={(theme) => ({
                    background: chrome.appBarBg || "#22374a",
                    backdropFilter: "blur(18px)",
                    borderBottom: `1px solid ${chrome.appBarBorder || alpha("#dfe9f4", 0.12)}`,
                    boxShadow: "0 12px 36px rgba(8, 23, 39, 0.12)",
                    zIndex: theme.zIndex.drawer - 1,
                    pl: { sm: "var(--portal-drawer-offset, calc(64px + 1px))" },
                })}
            >
                <Toolbar
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: { xs: MOBILE_TOOLBAR_H, sm: DESKTOP_TOOLBAR_H },
                        px: { xs: 1.75, sm: 2.25 },
                        pl: {
                            xs: 'calc(14px + var(--portal-mobile-toggle-space, 0px))',
                            sm: 2.25,
                        },
                        pt: { xs: 0.5, sm: 0.75 },
                        gap: 1,
                        position: 'relative',
                        transition: 'padding-left 180ms ease',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <StyledLink
                            component={RouterLink}
                            to={activeProductId === "booking" ? "/booking" : "/care"}
                            sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                        >
                            <img
                                src={Logo}
                                alt="Plany365"
                                style={{
                                    width: 28,
                                    height: 28,
                                    marginRight: 10,
                                    boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                    transition: "all 0.3s ease",
                                }}
                            />
                            <Typography
                                component="span"
                                sx={{
                                    display: { xs: 'none', sm: 'inline-flex' },
                                    alignItems: 'center',
                                    color: chrome.brandText || '#ffffff',
                                }}
                            >
                                <PlanyBrandMark />
                            </Typography>
                        </StyledLink>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: { xs: 1, sm: 1.5 },
                            pr: { xs: 0, sm: 1 },
                            flexWrap: { xs: 'wrap', sm: 'nowrap' },
                            ml: 'auto',
                        }}
                    >
                        {/* <WalletMenuItem /> */}
                        {/* Trial Notification (sm and up) */}
                        {/* <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                <StyledLink
                                    component={RouterLink}
                                    to="/biz/pricing"
                                    sx={{ color: 'white' }}
                                    underline="none"
                                    state={{ from: window.location.href }}
                                >
                                    <TrialNotification />
                                </StyledLink>
                            </Box> */}

                        {/* Main nav items */}
                        {providerUser.userId && !isTimelyApp && (
                            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center' }}>
                                <PlanyBot inline />
                            </Box>
                        )}
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1 }}>
                            <Tooltip title={switchToCustomerHelp}>
                                <Button
                                    component={activeProductId === 'booking' ? 'button' : RouterLink}
                                    to={activeProductId === 'booking' ? undefined : customerViewPath}
                                    onClick={activeProductId === 'booking' ? (event) => setCustomerMenuAnchor(event.currentTarget) : undefined}
                                    aria-label="Open customer view"
                                    aria-haspopup={activeProductId === 'booking' ? 'menu' : undefined}
                                    aria-expanded={customerMenuAnchor ? true : undefined}
                                    aria-controls={customerMenuAnchor ? 'business-customer-view-menu' : undefined}
                                    size="small"
                                    variant="outlined"
                                    startIcon={<SwapHorizRoundedIcon />}
                                    endIcon={activeProductId === 'booking' ? <KeyboardArrowDownRoundedIcon /> : undefined}
                                    sx={topActionSx}
                                >
                                    Customer View
                                </Button>
                            </Tooltip>
                            <Menu id="business-customer-view-menu" anchorEl={customerMenuAnchor}
                                open={Boolean(customerMenuAnchor)} onClose={closeCustomerMenu}>
                                {renderCustomerViewItems(closeCustomerMenu)}
                            </Menu>
                            <Button
                                component={RouterLink}
                                to={subscriptionsPath}
                                state={pricingLinkState}
                                size="small"
                                variant="outlined"
                                sx={topActionSx}
                            >
                                Subscriptions
                            </Button>
                            {!isTimelyApp && <Tooltip title="Call Plany Assist">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<PhoneInTalkIcon />}
                                    onClick={openCallMenu}
                                    sx={topActionSx}
                                    aria-controls="business-call-plany-assist-menu"
                                    aria-haspopup="true"
                                    aria-expanded={Boolean(callAnchor) ? "true" : undefined}
                                >
                                    Call Plany Assist
                                </Button>
                            </Tooltip>}

                            {/* {providerUser.userId && !providerUser.defaultBusinessId && (
                                    <StyledLink
                                        href="/biz/business"
                                        sx={navLinkSx}
                                        underline="none"
                                    >
                                        Create your first business profile
                                    </StyledLink>
                                )} */}

                            {!providerUser.userId && (
                                <>
                                    <StyledLink href="/login" sx={navLinkSx} underline="none">
                                        Login/Register
                                    </StyledLink>
                                    {/* <StartFreeTrialButton /> */}
                                </>
                            )}

                            {providerUser.userId && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PositionedMenu menu="Business" />
                                </Box>
                            )}
                            <Menu
                                id="business-call-plany-assist-menu"
                                anchorEl={callAnchor}
                                open={Boolean(callAnchor)}
                                onClose={closeCallMenu}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                keepMounted
                                disableScrollLock
                                PaperProps={{
                                    sx: (theme) => ({
                                        minWidth: 220,
                                        zIndex: theme.zIndex.modal + 1,
                                        borderRadius: 3,
                                        border: `1px solid ${alpha(theme.palette.common.black, 0.06)}`,
                                        boxShadow: "0 24px 50px rgba(9, 26, 42, 0.18)",
                                    }),
                                }}
                            >
                                {PHONES.map((phone) => (
                                    <MenuItem key={phone.tel} component="a" href={phone.tel} onClick={closeCallMenu}>
                                        <ListItemIcon>
                                            <PhoneInTalkIcon fontSize="small" />
                                        </ListItemIcon>
                                        {phone.label}
                                    </MenuItem>
                                ))}
                            </Menu>
                        </Box>

                        {/* Mobile menu trigger */}
                        <Box sx={{ display: { xs: 'flex', sm: 'none' }, alignItems: 'center', gap: 0.5 }}>
                            {providerUser.userId && !isTimelyApp && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PlanyBot inline />
                                </Box>
                            )}
                            {providerUser.userId && (
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <PositionedMenu menu="Business" showNotification={false} />
                                </Box>
                            )}
                            <IconButton
                                edge="end"
                                color="inherit"
                                aria-label="open business menu"
                                onClick={openMobileMenu}
                                size="large"
                            >
                                <MoreVertIcon />
                            </IconButton>
                            <Menu
                                anchorEl={mobileMenuAnchor}
                                open={Boolean(mobileMenuAnchor)}
                                onClose={closeMobileMenu}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                keepMounted
                                disableScrollLock
                                PaperProps={{
                                    sx: {
                                        minWidth: 260,
                                        borderRadius: 3,
                                        boxShadow: "0 24px 50px rgba(9, 26, 42, 0.18)",
                                    },
                                }}
                            >
                                {activeProductId === 'booking' && <ListSubheader>Customer view</ListSubheader>}
                                {activeProductId === 'booking' && renderCustomerViewItems(closeMobileMenu)}
                                {activeProductId !== 'booking' && <MenuItem component={RouterLink} to={customerViewPath} onClick={closeMobileMenu}>
                                    <ListItemIcon>
                                        <SwapHorizRoundedIcon fontSize="small" />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Customer View"
                                        secondary="Leave Business workspace"
                                    />
                                </MenuItem>}
                                <MenuItem
                                    component={RouterLink}
                                    to={subscriptionsPath}
                                    state={pricingLinkState}
                                    onClick={closeMobileMenu}
                                >
                                    Subscriptions
                                </MenuItem>
                                {!isTimelyApp && <>
                                <Divider />
                                <MenuItem disableRipple sx={{ pointerEvents: 'none', opacity: 0.9 }}>
                                    <ListItemIcon>
                                        <PhoneInTalkIcon fontSize="small" />
                                    </ListItemIcon>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Call Plany Assist
                                    </Typography>
                                </MenuItem>
                                {PHONES.map((phone) => (
                                    <MenuItem
                                        key={phone.tel}
                                        component="a"
                                        href={phone.tel}
                                        onClick={closeMobileMenu}
                                    >
                                        <ListItemText primary={phone.label} />
                                    </MenuItem>
                                ))}
                                </>}
                                {providerUser.userId && (
                                    <>
                                        <Divider />
                                        <MenuItem disableRipple onClick={(e) => e.stopPropagation()}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <NotificationBadge
                                                    currentStaffImage={currentStaffImage}
                                                    email={providerUser?.email}
                                                />
                                                <span>Alerts</span>
                                            </Box>
                                        </MenuItem>
                                    </>
                                )}
                            </Menu>
                        </Box>
                    </Box>
                </Toolbar>
                {showMetaRow && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            gap: 1,
                            minHeight: META_ROW_H,
                            px: { xs: 1.25, sm: 2.25 },
                            pb: 0.45,
                            backgroundColor: 'transparent',
                        }}
                    >
                        {businessName ? (
                            <StyledLink
                                component={RouterLink}
                                to="/biz/profile"
                                underline="none"
                                title={businessName}
                                sx={{
                                    color: 'rgba(255,255,255,0.84)',
                                    fontSize: { xs: '0.66rem', sm: '0.72rem' },
                                    fontWeight: 500,
                                    lineHeight: 1.2,
                                    minWidth: 0,
                                    maxWidth: { xs: '52vw', sm: '34vw' },
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        color: chrome.brandText || '#ffffff',
                                        textDecoration: 'underline',
                                    },
                                }}
                            >
                                {businessName}
                            </StyledLink>
                        ) : null}
                        {trialLabel && (
                            <Typography
                                variant="caption"
                                sx={{
                                    flexShrink: 0,
                                    color: 'rgba(235,243,250,0.88)',
                                    fontSize: { xs: '0.6rem', sm: '0.66rem' },
                                    fontWeight: 600,
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    borderRadius: 999,
                                    px: 0.75,
                                    py: 0.2,
                                    backgroundColor: chrome.actionBg || 'rgba(255,255,255,0.06)',
                                }}
                            >
                                {trialLabel}
                            </Typography>
                        )}
                    </Box>
                )}
            </AppBar>
            <Box
                aria-hidden
                sx={{
                    height: `${headerSpacerHeight}px`,
                }}
            />
        </>
    );
};

export default BusinessHeader;
